import _ from 'underscore';
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Toggle } from '@fluentui/react/lib/Toggle';

import { useDefaultEditorStyle } from '@bingads-webui-campaign-react/smart-component-util';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { TIMESPANS } from './time-utils';
import { SmartDayEditor } from './smart-day-editor';

const getStyles = () => ({
  hoursTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 20,
  },
  hourEditorsContainer: {
    margin: 0,
  },
});

export const UnStyledSmartHoursEditor = ({
  classes,
  i18n,
  id,
  // Header Toggle related Props
  // 1. Whether show header toggle
  showHeaderToggle,
  // 2. Default value for header toggle
  headerToggleChecked,
  // 3. Callback for header toggle change
  onHeaderToggleChange,
  // 4. Toggle label
  headerToggleLabel,
  // time values
  time,
  // Callback for time changing
  onTimeChange,
  // Style mode
  mode,
  // how long between two adjacent timeoptions,
  timeSpan,
}) => {
  const toggleText = i18n.getString(_TL_('Display on website'));
  const {
    sectionTitleClassName,
  } = useDefaultEditorStyle();

  const [isEditable, setEditable] = useState(showHeaderToggle ? headerToggleChecked : true);

  // Update a certain day and change siteConfig
  const onDayChange = useCallback(({
    day,
    timeSlots,
    isValid,
    isOpenAllDay,
  }) => {
    const updatedDays = _.map(time, d => (
      d.day === day ? {
        day,
        timeSlots,
        isValid,
        isOpenAllDay,
      } : d
    ));

    onTimeChange(updatedDays);
  }, [onTimeChange, time]);

  // Render each day's timeslots
  const days = useMemo(() => time.map(day => (
    <SmartDayEditor
      key={`edit-${day.day}`}
      data-name={day.day}
      i18n={i18n}
      isDisabled={!isEditable}
      mode={mode}
      value={day}
      onChange={onDayChange}
      timeSpan={timeSpan}
    />
  )), [onDayChange, time, i18n, isEditable, mode, timeSpan]);

  const editableChange = useCallback((e, checked) => {
    setEditable(checked);
    onHeaderToggleChange(checked);
  }, [onHeaderToggleChange]);

  const toggleLabel = headerToggleLabel || i18n.getString(_TL_('Business hours'));

  return (
    <div className="smart-hours-editor" data-id={id}>
      {
        showHeaderToggle
          && <Toggle
            label={
              <span className={sectionTitleClassName}>{toggleLabel}</span>
            }
            className={classNames(classes.hoursTitle, 'hours-toggle')}
            inlineLabel
            onText={toggleText}
            offText={toggleText}
            defaultChecked={isEditable}
            onChange={editableChange}
          />
      }
      <div className={classNames(classes.hourEditorsContainer, 'days-editor-container')}>
        { days }
      </div>
    </div>
  );
};

UnStyledSmartHoursEditor.propTypes = {
  classes: PropTypes.shape({
    hoursTitle: PropTypes.string.isRequired,
    hourEditorsContainer: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  id: PropTypes.string.isRequired,
  showHeaderToggle: PropTypes.bool,
  headerToggleLabel: PropTypes.string,
  headerToggleChecked: PropTypes.bool,
  onHeaderToggleChange: PropTypes.func,
  time: PropTypes.arrayOf(PropTypes.shape({
    day: PropTypes.string,
    isValid: PropTypes.bool,
    timeSlots: PropTypes.array,
  })).isRequired,
  onTimeChange: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  timeSpan: PropTypes.number,
};

UnStyledSmartHoursEditor.defaultProps = {
  showHeaderToggle: false,
  headerToggleLabel: '',
  headerToggleChecked: true,
  onHeaderToggleChange: _.noop,
  timeSpan: TIMESPANS.QUARTER,
};

export const SmartHoursEditor = withDefaultStyles(
  UnStyledSmartHoursEditor,
  getStyles
);
